import React from 'react';
import { Button, ButtonProps } from '@myua/kit';
import './styles.scss';

export type ToolbarButtonProps = Omit<ButtonProps, 'icon' | 'shape' | 'size' | 'variant'> & {
  icon: React.ReactNode;
};

/**
 * Кнопка панели действий над таблицей файлов: квадрат 32×32 с иконкой, а с
 * `children` — та же кнопка с подписью справа от иконки.
 *
 * Раньше каждая из тринадцати кнопок писала свою разметку на классах старого
 * слоя (круг 40×40, светлый фон, синяя иконка). Здесь тот же вид выражен
 * штатными пропами kit: `size="middle"` — это 32px из шкалы antd (24/32/40),
 * размер по макету, kit её не переопределяет.
 *
 * Вид — `variant="outlined"` без `color`: в макете (p23, p39) активная кнопка
 * ряда белая с нейтральной рамкой `--black-black-20`, а не с индиговой
 * заливкой; синяя в ней только иконка, подпись у Convert / Pack Playable /
 * Create link / Tags / Columns — обычная тёмная. Поэтому `color="indigo"`
 * здесь не подходит: он покрасил бы и рамку, и текст. Иконку красит
 * `styles.scss` токеном `--indigo-indigo-primary`. Задизейбленный вид
 * (серая заливка, серая иконка) даёт antd сам — он тоже совпадает с макетом.
 *
 * Форма — дефолтная (по макету кнопки квадратные, а не круглые): у antd
 * icon-only кнопка и без `shape` остаётся квадратом 32×32. Скругление — 6px
 * по макету, задано в `styles.scss` (у темы kit borderRadius 4px).
 *
 * Подписанные кнопки ряда (Convert, Pack Playable, Create link, Tags, Columns —
 * MYUA-16744, редизайн, раздел 5.3) сделаны этим же примитивом, а не вторым
 * компонентом-кнопкой: `children` уходит в kit Button, отступ между иконкой
 * и текстом antd ставит сам. Тултипов у них нет — подпись их заменяет.
 *
 * Всё остальное (id, className, disabled, onClick, onMouseEnter/onMouseLeave
 * от WithTooltipPopup) уходит в kit Button как есть, ref пробрасывается —
 * тултипы и поповеры клонируют триггер и ждут DOM-узел.
 */
export const ToolbarButton = React.forwardRef<HTMLButtonElement, ToolbarButtonProps>(
  function ToolbarButton({ icon, className, ...rest }, ref) {
    return (
      <Button
        ref={ref as React.Ref<HTMLButtonElement>}
        size="middle"
        variant="outlined"
        icon={icon}
        className={['ch-toolbar-button', className].filter(Boolean).join(' ')}
        {...rest}
      />
    );
  },
);
